import { Button, Clickable, Col, Input, Logo, Row, Text } from 'components';
import { firebase, openMobileAppLink, storage, useQuery } from 'core';
import { Api, theme, validateEmail } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AccountLocked, AppleLogin, FbLogin, GgLogin, LoginAttempts } from './';

const Auth = ({ handleLoading }) => {
  const [email, setEmail] = useState(''),
    emailInputRef = useRef(null),
    passwordInputRef = useRef(null),
    [emailErr, setEmailErr] = useState(null),
    [password, setPassword] = useState(''),
    [attempts, setAttempts] = useState(0),
    [passErr, setPassErr] = useState(null),
    [accountLockedVisible, setAccountLockedVisible] = useState(false),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    { t } = useTranslation(),
    { isMobile } = useSelector((state) => state.appState),
    disabled = password.length < 8 || !validateEmail(email),
    query = useQuery();

  useEffect(() => {
    const email = query.get('email');
    if (email) setEmail(email);
  }, []);

  useEffect(() => {
    setEmailErr(null);
  }, [email]);

  useEffect(() => {
    setPassErr(null);
  }, [password]);

  function handleModal() {
    setAccountLockedVisible(!accountLockedVisible);
  }

  async function login() {
    // reset field errors from the previous attempt
    setEmailErr(null);
    setPassErr(null);

    // silent: 4xx bodies (pwErr, err, locked) come back for inline rendering
    const res = await Api.post('/users/login', { email, password }, 'login', { silent: true });
    if (!res) return;

    if (res.pwErr) {
      setPassErr(res.pwErr);
      setAttempts(res.loginAttempts);
      passwordInputRef.current.focus();
      return;
    }

    if (res.locked) {
      setAttempts(3);
      return handleModal();
    }

    if (res.err) {
      emailInputRef.current.focus();
      return setEmailErr(res.err);
    }

    if (res.migratedUser) {
      return navigate('/activate-account', { state: { email } });
    }

    // error without a field-level payload (network, 5xx) — show global toast
    if (!res.token) {
      return dispatch({
        type: 'HANDLE_RES',
        payload: { err: t('error.somethingWentWrong') + '\n' + t('error.pleaseTryAgainLater') }
      });
    }

    await storage.setItem('token', res.token);

    firebase.logEvent('login', { method: 'email' });

    const navigateTo = query.get('navigateTo');
    const redirectUrl = query.get('redirect');

    // Use redirectUrl as navigateTo if present, strip leading slash since User.js adds one
    const finalNavigateTo = 
      redirectUrl
        ? decodeURIComponent(redirectUrl).replace(/^\//, '')
        : navigateTo || 
          (res.user.admin || process.env.REACT_APP_DEFAULT_TEAM_REDIRECT === 'false' 
            ? null 
            : `profile/${process.env.REACT_APP_DEFAULT_TEAM_USERNAME || 'fctokyo'}`);

    dispatch({
      type: 'SET_USER',
      payload: { ...res.user, navigateTo: finalNavigateTo }
    });
  }

  return (
    <React.Fragment>
      <Col
        pos={isMobile ? 'relative' : 'absolute'}
        pad={isMobile ? '24px 0 0' : '0'}
        noFlex
        bg="#fff"
        wid={isMobile ? '288px' : '296px'}
        ht="662px"
        top={isMobile ? '0' : '80px'}
        left={isMobile ? '0' : '812px'}
        start
        center
        centerAll
      >
        {!isMobile && <Logo wid="296px" />}

        {isMobile && <Text h2>{t('common.login')}</Text>}

        <Input
          inputRef={emailInputRef}
          marg="32px 0 0"
          wid="100%"
          placeholder={t('common.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailErr}
        />

        <Input
          inputRef={passwordInputRef}
          marg="28px 0 0"
          wid="100%"
          password
          placeholder={t('common.password')}
          value={password}
          error={passErr}
          onChange={(e) => setPassword(e.target.value)}
        />

        <LoginAttempts attempts={attempts} />

        <Button
          marg="28px 0 0"
          wid="100%"
          text={t('common.login')}
          bg={disabled ? theme.GREY_20 : theme.SECONDARY}
          app
          onClick={!disabled ? login : null}
          action="login"
        />

        <Clickable onClick={() => navigate('/forgot-password')}>
          <Text marg="24px 0 0" b1 bold>
            {t('auth.forgottenPassword')}
          </Text>
        </Clickable>

        <Text marg="24px 0 0">Or</Text>

        <FbLogin
          handleLoading={handleLoading}
          redirectUrl={query.get('redirect')}
          CustomButton={(props) => (
            <Button facebook onClick={props.onClick} marg="16px 0 0" text={t('auth.logIn')} />
          )}
        />

        <GgLogin
          handleLoading={handleLoading}
          redirectUrl={query.get('redirect')}
          CustomButton={(props) => (
            <Button google onClick={props.onClick} marg="16px 0 0" text={t('auth.logIn')} />
          )}
        />

        <AppleLogin
          redirectUrl={query.get('redirect')}
          CustomButton={(props) => (
            <Button apple onClick={props.onClick} marg="16px 0 0" text={t('auth.logIn')} />
          )}
        />

        <Text b1 marg="16px 0 0">
          {t('auth.getTheApp')}
        </Text>

        <Row wid="100%" gap="12px" marg="16px 0 0">
          <Clickable
            onClick={() => {
              openMobileAppLink(false);
            }}
          >
            <img src={require('assets/images/app-store.png')} alt="" />
          </Clickable>

          <Clickable
            onClick={() => {
              openMobileAppLink(true);
            }}
          >
            <img src={require('assets/images/google-play.png')} alt="" />
          </Clickable>
        </Row>

        <Clickable marg="20px 0 0" onClick={() => navigate('/signup')}>
          <Text b2>
            {t('auth.dontHaveAccount')}{' '}
            <Text spanned b2 med>
              {t('common.signUp')}
            </Text>
          </Text>
        </Clickable>
      </Col>
      <AccountLocked visible={accountLockedVisible} closeModal={handleModal} />
    </React.Fragment>
  );
};

export default Auth;
